import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { createAttributes } from '@atlassian/jira-forge-ui-analytics/src/common/utils/create-attributes/index.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { AnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/dashboard-background-script/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	useDashboardForgeResource,
	useDashboardResource,
} from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import type { SupportedBackgroundScript } from '../types.tsx';
import { getScriptsForTheInstalledGadgets } from '../utils/index.tsx';
import { ConnectBackgroundScript } from './connect/index.tsx';
import { ForgeBackgroundScript } from './forge/index.tsx';

export const BackgroundScriptsUnsafe = () => {
	const { data: forgeData } = useDashboardForgeResource();
	const { data: dashboardData } = useDashboardResource();
	const analyticsAttributes = useCallback(() => createAttributes(), []);

	const dashboardId = dashboardData?.id;
	const backgroundScriptsCombined = useMemo<SupportedBackgroundScript[]>(
		() =>
			getScriptsForTheInstalledGadgets({
				gadgets: dashboardData?.gadgets || [],
				connectBackgroundScripts: dashboardData?.backgroundScripts || [],
				forgeBackgroundScripts: forgeData?.backgroundScripts || [],
			}),
		[dashboardData?.gadgets, dashboardData?.backgroundScripts, forgeData?.backgroundScripts],
	);

	const hasScripts = backgroundScriptsCombined.length > 0;
	if (__SERVER__ || !hasScripts || dashboardId == null) {
		return null;
	}

	return (
		<Placeholder name="background-scripts-unsafe" fallback={null}>
			<Container>
				{backgroundScriptsCombined.map((script) => {
					switch (script.type) {
						case 'forge': {
							const module = script.item;
							return (
								<AnalyticsWrapper
									attributes={{
										...getAnalyticsAttributesFromExtension(script.item),
									}}
									key={`${module.id}_${module.properties.resourceUploadId}`}
								>
									<ForgeBackgroundScript item={module} />
								</AnalyticsWrapper>
							);
						}
						case 'connect': {
							const { appKey, moduleKey } = script.item;
							return (
								<ConnectBackgroundScript
									key={`connect-iFrame-${appKey}-${moduleKey}`}
									id="connect"
									packageName="dashboardBackgroundScript"
									item={script.item}
									analytics={analyticsAttributes()}
								/>
							);
						}
						default:
							return null;
					}
				})}
			</Container>
		</Placeholder>
	);
};

export const BackgroundScripts = () => {
	const getAttributes = useCallback((error: Error) => createAttributes({ error }), []);

	return (
		<JSErrorBoundary
			id="background-scripts"
			packageName="jiraDashboardBackgroundScripts"
			teamName="smite"
			fallback="unmount"
			attributes={getAttributes}
		>
			<BackgroundScriptsUnsafe />
		</JSErrorBoundary>
	);
};
export default BackgroundScripts;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'none',
});
